import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
// ✅ PHASE 4: Import getApiBaseUrl for dynamic API endpoint
import { getApiBaseUrl } from '../../config/region';

// Import all your existing images
import coffeeLeeImage from '../../assets/images/thumbnails/cafelee.webp';
import beerLeeImage from '../../assets/images/thumbnails/beerlee.webp';
import coworkLeeImage from '../../assets/images/thumbnails/coworkinglee.webp';
import gymLeeImage from '../../assets/images/thumbnails/gymlee.webp';
import foodCourtLeeImage from '../../assets/images/thumbnails/foodcourtlee.webp';
import nightClubLeeImage from '../../assets/images/thumbnails/nightclublee.webp';
import beachbarLeeImage from '../../assets/images/thumbnails/beachclublee.webp';
import fastFoodLeeImage from '../../assets/images/thumbnails/fastlee.webp';
import spaLeeImage from '../../assets/images/thumbnails/spalee.png';
import clinincleeImage from '../../assets/images/thumbnails/cliniclee.png';
import salonLeeImage from '../../assets/images/thumbnails/salonlee.png';
import HotelLeeImage from '../../assets/images/thumbnails/hotellee.webp';
import streetVendorMascot from '../../assets/Croppedmascotts/KebabLee.png';
import yogaLeeImage from '../../assets/Croppedmascotts/Yogee.jpg';

import { getCategoryIcon } from '../../utils/venueIconMapping';

// ===== GROUP DISPLAY NAME MAPPING =====
const GROUP_DISPLAY_NAMES = {
  'gc_food_drink': 'Food & Drink',
  'food_drink': 'Food & Drink',
  'Food Drink': 'Food & Drink',
  
  'gc_nightlife_entertainment': 'Nightlife & Entertainment',
  'nightlife_entertainment': 'Nightlife & Entertainment',
  'Gc_nightlife_entertainment': 'Nightlife & Entertainment',
  'Gc_nightlife_entertai': 'Nightlife & Entertainment',
  
  'gc_accommodation_hotels': 'Accommodation & Hotels',
  'accommodation_hotels': 'Accommodation & Hotels',
  'Gc_accommodation_hotels': 'Accommodation & Hotels',
  'Gc_accommodation_t': 'Accommodation & Travel',
  
  'gc_fitness_wellness': 'Fitness & Wellness',
  'fitness_wellness': 'Fitness & Wellness',
  'Gc_fitness_wellness': 'Fitness & Wellness',
  'gc_fitness': 'Fitness',
  'Fitness': 'Fitness',
  
  'gc_grocery_retail': 'Grocery & Retail',
  'grocery_retail': 'Grocery & Retail',
  'Gc_grocery_retail': 'Grocery & Retail',
  
  'gc_services': 'Services',
  'services': 'Services',
  
  'gc_entertainment': 'Entertainment',
  'entertainment': 'Entertainment',
  
  'gc_outdoor_recreation': 'Outdoor & Recreation',
  'outdoor_recreation': 'Outdoor & Recreation',
  
  'gc_education': 'Education',
  'education': 'Education',
  
  'gc_healthcare': 'Healthcare',
  'healthcare': 'Healthcare',
  
  'gc_transportation': 'Transportation',
  'transportation': 'Transportation',
  
  'gc_religion_spiritual': 'Religion & Spiritual',
  'religion_spiritual': 'Religion & Spiritual',
  
  'gc_general': 'General',
  'general': 'General',
  'General': 'General',
  
  'gc_accommodation': 'Accommodation',
  'accommodation': 'Accommodation',
  'Accommodation': 'Accommodation'
};

// ===== HELPER FUNCTION: Get proper display name =====
const getGroupDisplayName = (groupId) => {
  if (!groupId) return 'General';
  
  // Direct match
  if (GROUP_DISPLAY_NAMES[groupId]) {
    return GROUP_DISPLAY_NAMES[groupId];
  }
  
  // Try lowercase
  const lowerGroupId = groupId.toLowerCase();
  if (GROUP_DISPLAY_NAMES[lowerGroupId]) {
    return GROUP_DISPLAY_NAMES[lowerGroupId];
  }
  
  // Try without gc_ prefix
  const withoutPrefix = groupId.replace(/^gc_/i, '').replace(/^Gc_/i, '');
  if (GROUP_DISPLAY_NAMES[withoutPrefix]) {
    return GROUP_DISPLAY_NAMES[withoutPrefix];
  }
  
  // Fallback: Format it nicely
  return groupId
    .replace(/^gc_/i, '')
    .replace(/^Gc_/i, '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// ===== GROUP CONFIGURATION =====
const GROUP_CONFIG = {
  'gc_food_drink': {
    id: 'gc_food_drink',
    name: 'Food & Drink',
    displayName: 'Food & Drink',
    emoji: '🍽️',
    color: '#f59e0b',
    icon: '🍴'
  },
  'gc_fitness': {
    id: 'gc_fitness',
    name: 'Fitness',
    displayName: 'Fitness',
    emoji: '💪',
    color: '#dc2626',
    icon: '🏋️'
  },
  'gc_fitness_wellness': {
    id: 'gc_fitness_wellness',
    name: 'Fitness & Wellness',
    displayName: 'Fitness & Wellness',
    emoji: '💪',
    color: '#dc2626',
    icon: '🏋️'
  },
  'gc_accommodation': {
    id: 'gc_accommodation',
    name: 'Accommodation',
    displayName: 'Accommodation',
    emoji: '🏨',
    color: '#0891b2',
    icon: '🛏️'
  },
  'gc_grocery_retail': {
    id: 'gc_grocery_retail',
    name: 'Grocery & Retail',
    displayName: 'Grocery & Retail',
    emoji: '🛒',
    color: '#10b981',
    icon: '🛍️'
  },
  'gc_nightlife_entertainment': {
    id: 'gc_nightlife_entertainment',
    name: 'Nightlife & Entertainment',
    displayName: 'Nightlife & Entertainment',
    emoji: '🌃',
    color: '#7c3aed',
    icon: '🎉'
  },
  'gc_general': {
    id: 'gc_general',
    name: 'General',
    displayName: 'General',
    emoji: '📍',
    color: '#8b5cf6',
    icon: '📍'
  }
};

// ===== REPLACE THIS SECTION IN CategoryCarousel.jsx =====
// Location: Around line 50-150

// ===== CATEGORY DISPLAY CONFIG - EXTENDED =====
const CATEGORY_DISPLAY_CONFIG = {
  // FOOD & DRINK CATEGORIES
  'Cafe': { emoji: '☕', color: '#ea580c' },
  'Restaurant': { emoji: '🍴', color: '#f59e0b' },
  'Bar': { emoji: '🍷', color: '#92400e' },
  'Street Vendor': { emoji: '🛒', color: '#f59e0b' },
  
  // FITNESS & WELLNESS CATEGORIES
  'Fitness': { emoji: '💪', color: '#dc2626' },
  'Yoga Studio': { emoji: '🧘', color: '#a855f7' },
  'Gym': { emoji: '💪', color: '#dc2626' },
  'Spa': { emoji: '💆', color: '#ec4899' },
  
  // ACCOMMODATION CATEGORIES
  'Accommodation': { emoji: '🏨', color: '#0891b2' },
  'Hotel': { emoji: '🏨', color: '#0891b2' },
  'Resort': { emoji: '🏝️', color: '#06b6d4' },
  'Hostel': { emoji: '🏠', color: '#8b5cf6' },
  'Guesthouse': { emoji: '🏡', color: '#64748b' },
  
  // GENERAL/SERVICES CATEGORIES
  'General': { emoji: '📍', color: '#8b5cf6' },
  'Salon': { emoji: '💅', color: '#f472b6' },
  'Clinic': { emoji: '⚕️', color: '#10b981' },
  'Beach Club': { emoji: '🏖️', color: '#06b6d4' },
  'Nightclub': { emoji: '🌃', color: '#7c3aed' },
  'Pub': { emoji: '🍺', color: '#92400e' },
  'Coworking': { emoji: '💼', color: '#059669' },
  'Shop': { emoji: '🛍️', color: '#ec4899' },
  'Bakery': { emoji: '🍰', color: '#f59e0b' },
  'Dessert': { emoji: '🍨', color: '#ec4899' },
  
  // Add more as needed
  'Fast Food': { emoji: '🍔', color: '#ef4444' },
  'Seafood Restaurant': { emoji: '🦐', color: '#0ea5e9' },
  'Coffee Shop': { emoji: '☕', color: '#ea580c' },
  'Bakery (Retail)': { emoji: '🥐', color: '#f59e0b' },
  'Wine Bar': { emoji: '🍷', color: '#7c2d12' },
  'Cocktail Bar': { emoji: '🍸', color: '#78350f' },
  'Brewpub': { emoji: '🍺', color: '#92400e' },
  'Shisha Lounge': { emoji: '💨', color: '#78350f' },
  'Dessert Shop': { emoji: '🧁', color: '#ec4899' }
};

// ===== CATEGORY IMAGE MAPPING =====
const CATEGORY_IMAGE_MAP = {
  // FOOD & DRINK
  'Street Vendor': streetVendorMascot,
  'Bar': beerLeeImage,
  'Restaurant': foodCourtLeeImage,
  'Cafe': coffeeLeeImage,
  'Coffee Shop': coffeeLeeImage,
  'Fast Food': fastFoodLeeImage,
  'Seafood Restaurant': foodCourtLeeImage,
  'Bakery': foodCourtLeeImage,
  'Bakery (Retail)': foodCourtLeeImage,
  'Dessert Shop': coffeeLeeImage,
  
  // NIGHTLIFE
  'Beach Club': beachbarLeeImage,
  'Cocktail Bar': beerLeeImage,
  'Wine Bar': beerLeeImage,
  'Brewpub': beerLeeImage,
  'Pub': beerLeeImage,
  'Shisha Lounge': nightClubLeeImage,
  'Nightclub': nightClubLeeImage,
  
  // WORK & PRODUCTIVITY
  'Coworking': coworkLeeImage,
  
  // FITNESS & WELLNESS
  'Gym': gymLeeImage,
  'Fitness': gymLeeImage,
  'Spa': spaLeeImage,
  'Salon': salonLeeImage,
  'Yoga Studio': yogaLeeImage,
  'Clinic': clinincleeImage,
  
  // ACCOMMODATION
  'Hotel': HotelLeeImage,
  'Resort': HotelLeeImage,
  'Hostel': HotelLeeImage,
  'Guesthouse': HotelLeeImage,
  'Accommodation': HotelLeeImage
};

// ===== CATEGORY COLOR MAPPING =====
const CATEGORY_COLOR_MAP = {
  // FOOD & DRINK
  'Bar': '#92400e',
  'Restaurant': '#ea580c',
  'Cafe': '#f59e0b',
  'Coffee Shop': '#f59e0b',
  'Street Vendor': '#f59e0b',
  'Fast Food': '#ef4444',
  'Seafood Restaurant': '#0ea5e9',
  'Bakery': '#f59e0b',
  'Bakery (Retail)': '#f59e0b',
  'Dessert Shop': '#ec4899',
  
  // ACCOMMODATION
  'Hotel': '#0891b2',
  'Resort': '#06b6d4',
  'Hostel': '#8b5cf6',
  'Guesthouse': '#64748b',
  'Accommodation': '#0891b2',
  
  // NIGHTLIFE
  'Beach Club': '#06b6d4',
  'Cocktail Bar': '#78350f',
  'Wine Bar': '#7c2d12',
  'Brewpub': '#92400e',
  'Pub': '#92400e',
  'Shisha Lounge': '#78350f',
  'Nightclub': '#7c3aed',
  
  // WORK & FITNESS
  'Coworking': '#059669',
  'Gym': '#dc2626',
  'Fitness': '#dc2626',
  'Spa': '#ec4899',
  'Salon': '#f472b6',
  'Clinic': '#10b981',
  'Yoga Studio': '#a855f7',
  
  // GENERAL
  'General': '#8b5cf6',
  'Shop': '#ec4899'
};

const FITNESS_WELLNESS_CATEGORIES = ['Gym', 'Yoga Studio', 'Spa'];

const getCategoryImage = (type) => CATEGORY_IMAGE_MAP[type] || coffeeLeeImage;
const getCategoryColor = (type) => CATEGORY_COLOR_MAP[type] || '#f59e0b';

// Map group IDs to emojis and colors
const GROUP_EMOJI_MAP = {
  'gc_food_drink': '🍽️',
  'gc_nightlife_entertainment': '🌃',
  'gc_fitness_wellness': '💆',
  'gc_services': '💼',
  'gc_accommodation_travel': '🏨',
  'gc_religion_spiritual': '🕌'
};

const GROUP_COLOR_MAP = {
  'gc_food_drink': '#f59e0b',
  'gc_nightlife_entertainment': '#7c3aed',
  'gc_fitness_wellness': '#ec4899',
  'gc_services': '#059669',
  'gc_accommodation_travel': '#b45309',
  'gc_religion_spiritual': '#6b7280'
};

// Mapping between venue categories and groups
const VENUE_CATEGORY_TO_GROUP_MAP = {
  'Restaurant': 'gc_food_drink',
  'Cafe': 'gc_food_drink',
  'Street Vendor': 'gc_food_drink',
  'Street Vendor (Mobile)': 'gc_food_drink',
  'Bar': 'gc_food_drink',
  'Beach Club': 'gc_nightlife_entertainment',
  'Cocktail Bar': 'gc_nightlife_entertainment',
  'Wine Bar': 'gc_nightlife_entertainment',
  'Brewpub': 'gc_nightlife_entertainment',
  'Pub': 'gc_nightlife_entertainment',
  'Shisha Lounge': 'gc_nightlife_entertainment',
  'Gym': 'gc_fitness_wellness',
  'Yoga Studio': 'gc_fitness_wellness',
  'Spa': 'gc_fitness_wellness',
  'Hotel': 'gc_accommodation_travel',
  'Salon': 'gc_services',
  'Clinic': 'gc_services',
  'Daily Services': 'gc_services',
  'Coworking': 'gc_services'
};

// CategoryIcon Component
const CategoryIcon = ({ icon, size = '16px' }) => {
  const iconSrc = icon?.default || icon;
  
  if (typeof iconSrc === 'string' && (iconSrc.includes('.png') || iconSrc.includes('.svg') || iconSrc.includes('data:'))) {
    return (
      <img 
        src={iconSrc} 
        alt="category icon" 
        style={{ 
          width: size, 
          height: size, 
          objectFit: 'cover',
          display: 'block'
        }} 
      />
    );
  }
  return <span style={{ fontSize: size }}>{icon}</span>;
};

// ShimmerCard Component
const ShimmerCard = memo(() => (
  <div style={{
    minWidth: '140px',
    maxWidth: '140px',
    height: '120px',
    backgroundColor: '#f3f4f6',
    borderRadius: '16px',
    overflow: 'hidden',
    position: 'relative'
  }}>
    <div style={{
      position: 'absolute',
      top: 0,
      left: '-150%',
      width: '150%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
      animation: 'shimmer 1.5s infinite'
    }} />
    <style>{`
      @keyframes shimmer {
        from { left: -150%; }
        to { left: 150%; }
      }
    `}</style>
  </div>
));

ShimmerCard.displayName = 'ShimmerCard';

// TopCategoryCard Component
const TopCategoryCard = memo(({
  category,
  isSelected,
  index,
  onClick,
  count
}) => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={() => onClick(category.id)}
      style={{
        minWidth: '140px',
        maxWidth: '140px',
        height: '120px',
        backgroundColor: isSelected ? '#fef3c7' : 'white',
        borderRadius: '16px',
        border: isSelected ? '2px solid #f59e0b' : '1px solid #e5e7eb',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: isSelected 
          ? '0 4px 12px rgba(245, 158, 11, 0.3)' 
          : '0 2px 8px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        position: 'relative'
      }}
      whileHover={{ 
        scale: 1.05,
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
        y: -2
      }}
      whileTap={{ scale: 0.95 }}
    >
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: isSelected 
          ? `linear-gradient(135deg, ${category.color}30, ${category.color}15, transparent)`
          : `linear-gradient(135deg, ${category.color}15, ${category.color}08, transparent)`,
        zIndex: 1
      }} />
      
      <div style={{
        position: 'relative',
        zIndex: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '12px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            backgroundColor: isSelected ? '#f59e0b' : `${category.color}60`,
            borderRadius: '50%',
            backdropFilter: 'blur(4px)',
            border: isSelected ? '2px solid #ea580c' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            color: 'white',
            fontWeight: 'bold'
          }}>
            {isSelected && '✓'}
          </div>
          
          <div style={{
            fontSize: '32px',
            lineHeight: 1
          }}>
            {category.emoji}
          </div>
        </div>
        
        <div style={{
          borderRadius: '8px',
          padding: '6px 8px',
          background: isSelected 
            ? 'rgba(255, 255, 255, 1)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          border: isSelected 
            ? '1px solid #f59e0b' 
            : '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: isSelected 
            ? '0 2px 8px rgba(245, 158, 11, 0.3)' 
            : '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <h4 style={{
            fontSize: '11px',
            fontWeight: '600',
            color: isSelected ? '#ea580c' : '#1f2937',
            margin: 0,
            lineHeight: '1.1',
            textAlign: 'center',
            letterSpacing: '0.3px',
            textTransform: 'capitalize'
          }}>
            {category.displayName}  {/* ✅ FIXED: Use displayName */}
          </h4>
          {count > 0 && (
            <p style={{
              fontSize: '9px',
              color: isSelected ? '#92400e' : '#6b7280',
              margin: '2px 0 0 0',
              textAlign: 'center'
            }}>
              {t('category.venues', { count })}
            </p>
          )}
        </div>
      </div>
      
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: isSelected ? '4px' : '3px',
        background: isSelected 
          ? `linear-gradient(90deg, #f59e0b, #ea580c)` 
          : `linear-gradient(90deg, ${category.color}, ${category.color}80)`,
        zIndex: 4
      }} />
    </motion.div>
  );
});

TopCategoryCard.displayName = 'TopCategoryCard';

// PrimaryCategoryCard Component
const PrimaryCategoryCard = memo(({
  category,
  isSelected,
  isDisabled,
  index,
  onClick
}) => {
  const { t } = useTranslation();
  const handleClick = useCallback(() => {
    if (!isDisabled) {
      onClick(category.id);
    }
  }, [isDisabled, onClick, category.id]);

  const cardStyles = useMemo(() => ({
    minWidth: '140px',
    maxWidth: '140px',
    height: '120px',
    backgroundColor: isSelected ? '#fef3c7' : 'white',
    borderRadius: '16px',
    border: isSelected ? '2px solid #f59e0b' : '1px solid #e5e7eb',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: isSelected 
      ? '0 4px 12px rgba(245, 158, 11, 0.3)' 
      : '0 2px 8px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    position: 'relative',
    opacity: isDisabled ? 0.4 : 1,
    filter: isDisabled ? 'grayscale(100%)' : 'none',
    pointerEvents: isDisabled ? 'none' : 'auto'
  }), [isSelected, isDisabled]);

  const backgroundImageStyles = useMemo(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url(${category.image})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    opacity: isSelected ? 0.9 : 1,
    zIndex: 2
  }), [category.image, isSelected]);

  const overlayStyles = useMemo(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: isSelected 
      ? `linear-gradient(135deg, ${category.color}30, ${category.color}15, transparent)`
      : `linear-gradient(135deg, ${category.color}15, ${category.color}08, transparent)`,
    zIndex: 1
  }), [category.color, isSelected]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={handleClick}
      style={cardStyles}
      whileHover={!isDisabled ? { 
        scale: 1.05, 
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
        y: -2 
      } : {}}
      whileTap={!isDisabled ? { scale: 0.95 } : {}}
    >
      <div style={backgroundImageStyles} />
      <div style={overlayStyles} />
      
      <div style={{
        position: 'relative',
        zIndex: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '12px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            backgroundColor: isSelected ? '#f59e0b' : `${category.color}60`,
            borderRadius: '50%',
            backdropFilter: 'blur(4px)',
            border: isSelected ? '2px solid #ea580c' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            color: 'white',
            fontWeight: 'bold',
            transition: 'all 0.3s ease'
          }}>
            {isSelected && '✓'}
          </div>
          
          <div style={{
            fontSize: '20px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            padding: '4px',
            backdropFilter: 'blur(4px)',
            position: 'relative'
          }}>
            <CategoryIcon icon={category.icon} size="20px" />
          </div>
        </div>
        
        <div style={{
          borderRadius: '8px',
          padding: '6px 8px',
          background: isSelected 
            ? 'rgba(255, 255, 255, 1)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          border: isSelected 
            ? '1px solid #f59e0b' 
            : '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: isSelected 
            ? '0 2px 8px rgba(245, 158, 11, 0.3)' 
            : '0 2px 8px rgba(0, 0, 0, 0.1)',
          marginTop: 'auto'
        }}>
          <h4 style={{
            fontSize: '12px',
            fontWeight: '600',
            color: isSelected ? '#ea580c' : '#1f2937',
            margin: 0,
            lineHeight: '1.1',
            textAlign: 'center',
            letterSpacing: '0.3px',
            textTransform: 'uppercase'
          }}>
            {category.title}
          </h4>
          {category.count > 0 && (
            <p style={{
              fontSize: '9px',
              color: isSelected ? '#92400e' : '#6b7280',
              margin: '2px 0 0 0',
              textAlign: 'center'
            }}>
              {t('category.venues', { count: category.count })}
            </p>
          )}
        </div>
      </div>
      
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: isSelected ? '4px' : '3px',
        background: isSelected 
          ? `linear-gradient(90deg, #f59e0b, #ea580c)` 
          : `linear-gradient(90deg, ${category.color}, ${category.color}80)`,
        zIndex: 4
      }} />
    </motion.div>
  );
}, (prevProps, nextProps) => {
  return prevProps.category.id === nextProps.category.id && 
         prevProps.isSelected === nextProps.isSelected && 
         prevProps.isDisabled === nextProps.isDisabled && 
         prevProps.index === nextProps.index;
});

PrimaryCategoryCard.displayName = 'PrimaryCategoryCard';

// SubCategoryCard Component
const SubCategoryCard = memo(({
  subCategory,
  isSelected,
  index,
  onClick
}) => {
  const { t } = useTranslation();
  const handleClick = useCallback(() => {
    onClick(subCategory.id);
  }, [onClick, subCategory.id]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={handleClick}
      style={{
        minWidth: '80px',
        maxWidth: '80px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px'
      }}
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.95 }}
    >
      <div style={{
        width: '80px',
        height: '60px',
        backgroundColor: isSelected ? '#fef3c7' : 'white',
        borderRadius: '10px',
        border: isSelected ? '2px solid #f59e0b' : '1px solid #e5e7eb',
        boxShadow: isSelected 
          ? '0 2px 8px rgba(245, 158, 11, 0.3)' 
          : '0 1px 4px rgba(0, 0, 0, 0.06)',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '4px'
      }}>
        {isSelected && (
          <div style={{
            position: 'absolute',
            top: '2px',
            left: '2px',
            width: '12px',
            height: '12px',
            backgroundColor: '#f59e0b',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '7px',
            color: 'white',
            fontWeight: 'bold',
            border: '1px solid white',
            zIndex: 10
          }}>
            ✓
          </div>
        )}
        
        <div style={{
          fontSize: '16px',
          marginBottom: '4px'
        }}>
          <CategoryIcon icon={subCategory.icon} size="50px" />
        </div>
      </div>
      
      <div style={{
        width: '80px',
        textAlign: 'center',
        position: 'relative',
        cursor: 'pointer'
      }} title={subCategory.title}>
        <h5 style={{
          fontSize: '9px',
          fontWeight: '600',
          color: isSelected ? '#ea580c' : '#1f2937',
          margin: '0 0 1px 0',
          textAlign: 'center',
          lineHeight: '1.1'
        }}>
          {subCategory.title.length > 15 ? `${subCategory.title.slice(0, 15)}...` : subCategory.title}
        </h5>
        <p style={{
          fontSize: '7px',
          color: isSelected ? '#92400e' : '#6b7280',
          margin: 0,
          textAlign: 'center'
        }}>
          {t('category.venues', { count: subCategory.count })}
        </p>
      </div>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  return prevProps.subCategory.id === nextProps.subCategory.id && 
         prevProps.isSelected === nextProps.isSelected && 
         prevProps.index === nextProps.index;
});

SubCategoryCard.displayName = 'SubCategoryCard';

// QuickFilterButton Component
const QuickFilterButton = memo(({ 
  filter, 
  isSelected, 
  index,
  onClick,
  isChild = false,
  parentId = null,
  expandedParentFilters
}) => {
  const handleClick = useCallback(() => {
    onClick(filter.id);
  }, [filter.id, onClick]);

  const isExpanded = parentId ? expandedParentFilters.includes(parentId) : false;
  const hasChildren = filter.isParent && filter.children && filter.children.length > 0;

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        backgroundColor: isSelected ? '#fef3c7' : 'white',
        border: isSelected ? '2px solid #f59e0b' : '1px solid #d1d5db',
        borderRadius: '16px',
        fontSize: '11px',
        fontWeight: '500',
        color: isSelected ? '#ea580c' : '#374151',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: isSelected 
          ? '0 2px 6px rgba(245, 158, 11, 0.3)' 
          : '0 1px 3px rgba(0, 0, 0, 0.1)',
        minWidth: 'fit-content',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        position: 'relative',
        marginLeft: isChild ? '0px' : '0',
        marginTop: isChild ? '0px' : '0'
      }}
    >
      {isSelected && (
        <span style={{
          position: 'absolute',
          top: '-4px',
          left: '-4px',
          width: '12px',
          height: '12px',
          backgroundColor: '#f59e0b',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '7px',
          color: 'white',
          fontWeight: 'bold',
          border: '1px solid white'
        }}>
          ✓
        </span>
      )}
      
      <span style={{ fontSize: '12px' }}>
        <CategoryIcon icon={filter.icon} size="18px" />
      </span>
      <span>{filter.label}</span>
      {filter.count && (
        <span style={{
          fontSize: '9px',
          backgroundColor: isSelected ? '#f59e0b' : '#f3f4f6',
          color: isSelected ? 'white' : '#6b7280',
          padding: '1px 4px',
          borderRadius: '8px',
          marginLeft: '4px',
          fontWeight: '600'
        }}>
          {filter.count}
        </span>
      )}
      {hasChildren && (
        <span style={{
          fontSize: '10px',
          marginLeft: '4px',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }}>
          ▼
        </span>
      )}
    </motion.button>
  );
}, (prevProps, nextProps) => {
  return prevProps.filter.id === nextProps.filter.id && 
         prevProps.isSelected === nextProps.isSelected && 
         prevProps.index === nextProps.index && 
         prevProps.isChild === nextProps.isChild &&
         prevProps.expandedParentFilters === nextProps.expandedParentFilters;
});

QuickFilterButton.displayName = 'QuickFilterButton';

// Fetch all venues with pagination
const fetchCategoryVenues = async (region = 'th') => {
  console.log(`🔄 Fetching venues & vendors for region: ${region}`);
  let allVenues = [];
  let allVendors = [];
  const token = localStorage.getItem('venue_app_token');

  try {
    // ===== FETCH VENUES =====
    // ✅ PHASE 4: Use getApiBaseUrl() instead of hardcoded domain
    const firstVenueResponse = await fetch(`${getApiBaseUrl()}/venues?page=1&limit=1000`, {
      headers: {
        'Content-Type': 'application/json',
        'x-region': region,
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });

    if (firstVenueResponse.ok) {
      const firstResult = await firstVenueResponse.json();
      if (firstResult.success && firstResult.data) {
        allVenues = [...firstResult.data];
        const totalPages = firstResult.pagination?.totalPages || 1;

        if (totalPages > 1) {
          const remainingPages = [];
          for (let page = 2; page <= totalPages; page++) {
            remainingPages.push(
              // ✅ PHASE 4: Use getApiBaseUrl() instead of hardcoded domain
              fetch(`${getApiBaseUrl()}/venues?page=${page}&limit=1000`, {
                headers: {
                  'Content-Type': 'application/json',
                  'x-region': region,
                  ...(token && { 'Authorization': `Bearer ${token}` })
                }
              })
              .then(res => res.json())
              .then(data => data.data || [])
            );
          }
          const allRemainingVenues = await Promise.all(remainingPages);
          allVenues = [...allVenues, ...allRemainingVenues.flat()];
        }
      }
    }

    // ===== FETCH STREET VENDORS =====
    // ✅ PHASE 4: Use getApiBaseUrl() instead of hardcoded domain
    const vendorResponse = await fetch(`${getApiBaseUrl()}/street-vendors?page=1&limit=50`, {
      headers: {
        'Content-Type': 'application/json',
        'x-region': region,
        ...(token && { 'Authorization': `Bearer ${token}` })
      }
    });

    if (vendorResponse.ok) {
      const vendorResult = await vendorResponse.json();
      if (vendorResult.success && vendorResult.data) {
        allVendors = vendorResult.data.map(vendor => {
          // Determine cuisine category for vendors
          const cuisineTags = vendor.cuisineTags?.split(';') || [];
          let vendorCategory = 'Street Vendor';
          let vendorGroupId = 'gc_food_drink';

          // Map vendor cuisine to category
          if (cuisineTags.some(tag => tag.toLowerCase().includes('coffee'))) {
            vendorCategory = 'Cafe';
          } else if (cuisineTags.some(tag => tag.toLowerCase().includes('noodle'))) {
            vendorCategory = 'Restaurant';
          }

          return {
            ...vendor,
            isStreetVendor: true,
            venueType: 'street_vendor',
            venuecategorydisplayname: vendorCategory,  // FIX: Correct field name
            venue_category_display: vendorCategory,
            venuetypedisplay: 'Street Vendor',
            venue_type_display: 'Street Vendor',
            groupid: vendorGroupId,  // FIX: Add missing groupid
            venuegroup: vendorGroupId,
            name: vendor.vendorName || vendor.accountName,
            lng: vendor.currentLocation?.coordinates?.[0] || vendor.longitude,
            lat: vendor.currentLocation?.coordinates?.[1] || vendor.latitude,
          };
        });
      }
    }

    const combined = [...allVenues, ...allVendors];
    console.log(`✅ Loaded ${allVenues.length} venues + ${allVendors.length} vendors for region ${region}`);
    return combined;

  } catch (error) {
    console.error('Error fetching venues & vendors:', error);
    return allVenues;
  }
};

// Main CategoryCarousel Component
const CategoryCarousel = ({
  onCategorySelect,
  selectedPrimaryCategory,
  selectedSubCategory,
  region = 'th' // Add region prop with default
}) => {
  const { t } = useTranslation();
  // State management
  const [selectedTopCategory, setSelectedTopCategory] = useState(null);
  const [selectedPrimaryCategories, setSelectedPrimaryCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [selectedQuickFilters, setSelectedQuickFilters] = useState([]);
  
  const [primaryCategories, setPrimaryCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [expandedParentFilters, setExpandedParentFilters] = useState([]);

  // Fetch venues with region parameter
  const { data: apiVenues = [], isLoading: isLoadingVenues, error } = useQuery({
  queryKey: ['category-venues', region],
  queryFn: () => fetchCategoryVenues(region),
  staleTime: 10 * 60 * 1000,
  cacheTime: 30 * 60 * 1000,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
});

// ===== STEP 1: DEFINE generateQuickFilters FIRST (before it's used) =====
const generateQuickFilters = useCallback((categoryType, venues) => {
  const filters = [];

  // ===== EXISTING FILTERS =====
  /* field removed - no Neon equivalent yet for PubWifi, Charging_Ports, Outdoor_seating, Veg_only */
  const wifiCount = 0;
  const chargingCount = 0;
  const outdoorCount = 0;
  const openLateCount = venues.filter(v => v.is_open_late === true).length;
  const vegOnlyCount = 0;

  if (wifiCount > 0) filters.push({
    id: 'wifi',
    label: t('category.filterWifiAvailable'),
    icon: '📶',
    count: wifiCount
  });

  if (chargingCount > 0) filters.push({
    id: 'charging',
    label: t('venue.chargingPorts'),
    icon: '🔌',
    count: chargingCount
  });

  if (outdoorCount > 0) filters.push({
    id: 'outdoor',
    label: t('venue.outdoorSeating'),
    icon: '🌳',
    count: outdoorCount
  });

  if (openLateCount > 0) filters.push({
    id: 'openlate',
    label: t('filters.labelOpenLate'),
    icon: '🌙',
    count: openLateCount
  });

  if (vegOnlyCount > 0) filters.push({
    id: 'vegonly',
    label: t('category.filterVegOnly'),
    icon: '🥬',
    count: vegOnlyCount
  });

  // ===== NEW FILTERS FROM EXTENDED DATA =====
  const alcoholCount = venues.filter(v => v.serves_alcohol === true).length;
  const petFriendlyCount = venues.filter(v => v.pet_policy && v.pet_policy.toLowerCase().includes('allowed')).length;
  const bookingCount = venues.filter(v => v.takes_bookings === true).length;
  /* field removed - no Neon equivalent yet for Nomad_friendly_score, BudgetFriendly */
  const nomadFriendlyCount = 0;
  const budgetFriendlyCount = 0;
  const premiumCount = 0;

  if (alcoholCount > 0) filters.push({
    id: 'alcohol',
    label: t('category.filterAlcoholServed'),
    icon: '🍸',
    count: alcoholCount
  });

  if (petFriendlyCount > 0) filters.push({
    id: 'petfriendly',
    label: t('category.filterPetFriendly'),
    icon: '🐾',
    count: petFriendlyCount
  });

  if (bookingCount > 0) filters.push({
    id: 'bookings',
    label: t('category.filterTakesBookings'),
    icon: '📅',
    count: bookingCount
  });

  if (nomadFriendlyCount > 0) filters.push({
    id: 'nomad',
    label: t('category.filterNomadFriendly'),
    icon: '🌐',
    count: nomadFriendlyCount
  });

  if (budgetFriendlyCount > 0) filters.push({
    id: 'budget',
    label: t('category.filterBudgetFriendly'),
    icon: '💰',
    count: budgetFriendlyCount
  });

  if (premiumCount > 0) filters.push({
    id: 'premium',
    label: t('category.filterPremium'),
    icon: '👑',
    count: premiumCount
  });

  return filters;
}, [t]);

// ===== STEP 2: DEFINE generatePrimaryCategories (which doesn't use generateQuickFilters) =====
const generatePrimaryCategories = useCallback((venues, selectedGroupId = null) => {
  console.log(`🔍 Generating primary categories for group: ${selectedGroupId}`);

  let filteredVenues = venues;

  // Filter venues by selected group
  if (selectedGroupId) {
    filteredVenues = venues.filter(venue => {
      const venueGroupId = venue.groupid || venue.venuegroup;
      return venueGroupId === selectedGroupId;
    });
    console.log(`  Filtered to ${filteredVenues.length} venues in group ${selectedGroupId}`);
  }

  // ===== Get unique VENUE CATEGORIES (not groups) =====
  const categoryStats = {};
  
  filteredVenues.forEach(venue => {
    // Get the venue category (Restaurant, Cafe, Bar, Hotel, etc.)
    const categoryName = venue.venuecategorydisplayname || venue.venue_category_display;
    
    if (categoryName) {
      if (!categoryStats[categoryName]) {
        categoryStats[categoryName] = {
          venues: [],
          types: new Set()
        };
      }
      
      categoryStats[categoryName].venues.push(venue);
      
      // Track venue types within this category
      const venueType = venue.venuetypedisplay || venue.venue_type_display;
      if (venueType) {
        categoryStats[categoryName].types.add(venueType);
      }
    }
  });

  console.log('  Category stats:', Object.keys(categoryStats));

  // Map to display format
  const primaryCategories = Object.entries(categoryStats)
    .map(([categoryName, stats]) => {
      const config = CATEGORY_DISPLAY_CONFIG[categoryName] || {};
      const categoryImage = CATEGORY_IMAGE_MAP[categoryName] || coffeeLeeImage;
      const categoryColor = CATEGORY_COLOR_MAP[categoryName] || '#f59e0b';
      
      return {
        id: `category-${categoryName.toLowerCase().replace(/\s+/g, '-')}`,
        title: categoryName,
        emoji: config.emoji || '📍',
        color: categoryColor,
        image: categoryImage,
        icon: getCategoryIcon(categoryName),
        count: stats.venues.length,
        venues: stats.venues,
        groupId: selectedGroupId,
        venueTypes: Array.from(stats.types) // Store available venue types
      };
    })
    .filter(cat => cat.count > 0)
    .sort((a, b) => b.count - a.count);

  console.log('✅ Primary categories:', primaryCategories.map(c => `${c.title} (${c.count})`));
  setPrimaryCategories(primaryCategories);
}, []);

// ===== STEP 3: Generate top categories (groups) =====
const topCategories = useMemo(() => {
  if (!apiVenues || apiVenues.length === 0) {
    console.log('❌ No venues available for top categories');
    return [];
  }

  console.log('📊 Processing', apiVenues.length, 'venues');

  // Collect group statistics
  const groupStats = {};

  apiVenues.forEach(venue => {
    // Read groupid from your data
    const groupId = venue.groupid || venue.venuegroup || 'gc_general';
    
    if (!groupStats[groupId]) {
      groupStats[groupId] = {
        count: 0,
        ratings: [],
        categories: new Set()
      };
    }

    groupStats[groupId].count++;
    
    // Track categories in each group
    const categoryName = venue.venuecategorydisplayname || venue.venue_category_display;
    if (categoryName) {
      groupStats[groupId].categories.add(categoryName);
    }

    if (venue.Rating) {
      groupStats[groupId].ratings.push(venue.Rating);
    }
  });

  console.log('📍 Groups found:', Object.keys(groupStats));

  // ✅ FIX: Convert to display format with PROPER NAMES
  const categories = Object.entries(groupStats)
    .map(([groupId, stats]) => {
      const config = GROUP_CONFIG[groupId];
      const avgRating = stats.ratings.length > 0 
        ? parseFloat((stats.ratings.reduce((a, b) => a + (b || 0), 0) / stats.ratings.length)).toFixed(1)
        : 'N/A';

      // ✅ Use getGroupDisplayName function for proper display
      const displayName = getGroupDisplayName(groupId);
      
      // Extract short name from display name
      const shortName = displayName.split('&')[0].trim(); // "Food & Drink" → "Food"

      return {
        id: groupId,
        name: shortName,  // ✅ Short name for compact display
        displayName: displayName,  // ✅ Full display name
        emoji: config?.emoji || '📍',
        color: config?.color || '#f59e0b',
        count: stats.count,
        avgRating: avgRating,
        categories: Array.from(stats.categories),
        icon: config?.icon || '📍'
      };
    })
    .sort((a, b) => b.count - a.count);

  console.log('✅ Top categories generated:', categories.map(c => `${c.displayName} (${c.count})`));
  return categories;
}, [apiVenues]);

const isLoading = isLoadingVenues;

// ===== STEP 4: Regenerate primary categories when top category or venues change =====
useEffect(() => {
  if (apiVenues.length > 0) {
    generatePrimaryCategories(apiVenues, selectedTopCategory);
  }
}, [apiVenues, selectedTopCategory, generatePrimaryCategories]);

// ===== STEP 5: Generate sub-categories when primary categories change =====
// NOW generateQuickFilters is already defined, so this will work
useEffect(() => {
  if (selectedPrimaryCategories.length === 0) {
    setSubCategories([]);
    setSelectedSubCategories([]);
    return;
  }

  setSelectedSubCategories([]);
  
  const selectedVenues = primaryCategories
    .filter(cat => selectedPrimaryCategories.includes(cat.id))
    .flatMap(cat => cat.venues);

  // Group by VENUE TYPE (not category)
  const venueTypeStats = {};
  
  selectedVenues.forEach(venue => {
    const venueType = venue.venuetypedisplay || venue.venue_type_display;
    
    if (venueType) {
      if (!venueTypeStats[venueType]) {
        venueTypeStats[venueType] = [];
      }
      venueTypeStats[venueType].push(venue);
    }
  });

  const dynamicSubCategories = Object.entries(venueTypeStats)
    .map(([typeName, matchingVenues], index) => {
      return {
        id: `sub-${index + 1}`,
        title: typeName,
        image: getCategoryImage(typeName),
        color: getCategoryColor(typeName),
        icon: getCategoryIcon(typeName),
        count: matchingVenues.length,
        venues: matchingVenues,
        quickFilters: generateQuickFilters(typeName, matchingVenues) // ✅ NOW THIS WORKS
      };
    })
    .sort((a, b) => b.count - a.count);
  
  console.log('✅ Sub-categories (venue types):', dynamicSubCategories.map(c => `${c.title} (${c.count})`));
  setSubCategories(dynamicSubCategories);
}, [selectedPrimaryCategories, primaryCategories, generateQuickFilters]);

  // Update parent component with selections
  useEffect(() => {
    if (!onCategorySelect) return;

    const primaryTitles = selectedPrimaryCategories
      .map(id => primaryCategories.find(cat => cat.id === id)?.title)
      .filter(Boolean);

    const subTitles = selectedSubCategories
      .map(id => subCategories.find(cat => cat.id === id)?.title)
      .filter(Boolean);

    const selectedHealthyFoodLevels = selectedQuickFilters
      .filter(filterId => filterId.startsWith('healthyfood-'))
      .map(filterId => filterId.replace('healthyfood-', '').replace(/_/g, ' '));

    const selectedOpenLate = selectedQuickFilters.includes('openlate');
    const selectedVegOnly = selectedQuickFilters.includes('vegonly');
    const selectedOutdoorSeating = selectedQuickFilters.includes('outdoorseatingfilter');

    onCategorySelect(
      primaryTitles,
      subTitles,
      [],
      [],
      [],
      selectedHealthyFoodLevels,
      selectedOpenLate,
      selectedVegOnly,
      selectedOutdoorSeating
    );
  }, [
    selectedPrimaryCategories,
    selectedSubCategories,
    selectedQuickFilters,
    primaryCategories,
    subCategories,
    onCategorySelect
  ]);

  // Handle top category selection
  const handleTopCategorySelect = useCallback((topCategoryId) => {
    if (selectedTopCategory === topCategoryId) {
      setSelectedTopCategory(null);
      setSelectedPrimaryCategories([]);
      setSelectedSubCategories([]);
      setSelectedQuickFilters([]);
    } else {
      setSelectedTopCategory(topCategoryId);
      setSelectedPrimaryCategories([]);
      setSelectedSubCategories([]);
      setSelectedQuickFilters([]);
    }
  }, [selectedTopCategory]);

  // Handle primary category selection
  const handlePrimaryCategorySelect = useCallback((categoryId) => {
    setSelectedPrimaryCategories(prev => {
      const selectedCategory = primaryCategories.find(cat => cat.id === categoryId);
      
      if (prev.includes(categoryId)) {
        const updated = prev.filter(id => id !== categoryId);
        if (updated.length === 0) {
          setSelectedSubCategories([]);
          setSelectedQuickFilters([]);
        }
        return updated;
      } else {
        const selectedCategoryTitle = selectedCategory?.title;
        
        if (FITNESS_WELLNESS_CATEGORIES.includes(selectedCategoryTitle)) {
          const hasNonFitnessCategories = prev.some(id => {
            const existingCategory = primaryCategories.find(cat => cat.id === id);
            return existingCategory && !FITNESS_WELLNESS_CATEGORIES.includes(existingCategory.title);
          });
          
          if (hasNonFitnessCategories) {
            return prev;
          }
        } else {
          const hasFitnessCategories = prev.some(id => {
            const existingCategory = primaryCategories.find(cat => cat.id === id);
            return existingCategory && FITNESS_WELLNESS_CATEGORIES.includes(existingCategory.title);
          });
          
          if (hasFitnessCategories) {
            return prev;
          }
        }
        
        setSelectedSubCategories([]);
        setSelectedQuickFilters([]);
        return [...prev, categoryId];
      }
    });
  }, [primaryCategories]);

  const handleSubCategorySelect = useCallback((subCategoryId) => {
    setSelectedSubCategories(prev => {
      if (prev.includes(subCategoryId)) {
        const updated = prev.filter(id => id !== subCategoryId);
        if (updated.length === 0) {
          setSelectedQuickFilters([]);
        }
        return updated;
      } else {
        return [...prev, subCategoryId];
      }
    });
  }, []);

  const handleParentFilterToggle = useCallback((parentId) => {
    setExpandedParentFilters(prev => {
      if (prev.includes(parentId)) {
        return prev.filter(id => id !== parentId);
      } else {
        return [...prev, parentId];
      }
    });
  }, []);

  const handleChildFilterSelect = useCallback((childId) => {
    setSelectedQuickFilters(prev => {
      if (prev.includes(childId)) {
        return prev.filter(id => id !== childId);
      } else {
        return [...prev, childId];
      }
    });
  }, []);

  const handleQuickFilterSelect = useCallback((filterId) => {
    if (filterId === 'healthyfoodparent') {
      handleParentFilterToggle(filterId);
    } else if (filterId.startsWith('healthyfood-')) {
      handleChildFilterSelect(filterId);
    } else {
      setSelectedQuickFilters(prev => {
        if (prev.includes(filterId)) {
          return prev.filter(id => id !== filterId);
        } else {
          return [...prev, filterId];
        }
      });
    }
  }, [handleParentFilterToggle, handleChildFilterSelect]);

  const clearAllFilters = useCallback(() => {
    setSelectedTopCategory(null);
    setSelectedPrimaryCategories([]);
    setSelectedSubCategories([]);
    setSelectedQuickFilters([]);
  }, []);

  const allQuickFilters = useMemo(() => {
    const filters = [];

    if (selectedPrimaryCategories.length > 0) {
      const selectedPrimaryVenues = primaryCategories
        .filter(cat => selectedPrimaryCategories.includes(cat.id))
        .flatMap(cat => cat.venues);

      if (selectedPrimaryVenues.length > 0) {
        const primaryQuickFilters = generateQuickFilters('primary', selectedPrimaryVenues);
        filters.push(...primaryQuickFilters);
      }
    }

    if (selectedSubCategories.length > 0) {
      const subQuickFilters = subCategories
        .filter(sub => selectedSubCategories.includes(sub.id))
        .flatMap(sub => sub.quickFilters);
      filters.push(...subQuickFilters);
    }

    return filters.filter((filter, index, self) => 
      index === self.findIndex(f => f.id === filter.id)
    );
  }, [selectedPrimaryCategories, selectedSubCategories, primaryCategories, subCategories, generateQuickFilters]);

  const totalActiveFilters = useMemo(() => 
    selectedPrimaryCategories.length + selectedSubCategories.length + selectedQuickFilters.length
  , [selectedPrimaryCategories.length, selectedSubCategories.length, selectedQuickFilters.length]);

  const hasActiveFilters = totalActiveFilters > 0;

  if (error) {
    console.error('Error loading category venues:', error);
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#ef4444' }}>
        <p>{t('category.loadError')}</p>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      {/* TOP CATEGORIES SECTION */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#111827',
          margin: 0
        }}>
          {t('category.exploreBy')}
          {selectedTopCategory && (
            <span style={{
              marginLeft: '8px',
              fontSize: '12px',
              fontWeight: '600',
              color: '#f59e0b',
              backgroundColor: '#fef3c7',
              padding: '2px 8px',
              borderRadius: '12px'
            }}>
              {topCategories.find(c => c.id === selectedTopCategory)?.name}
            </span>
          )}
        </h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AnimatePresence>
            {hasActiveFilters && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={clearAllFilters}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 10px',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fca5a5',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#dc2626',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 3px rgba(220, 38, 38, 0.1)'
                }}
              >
                <span style={{ fontSize: '12px' }}>✕</span>
                {t('category.clearAll')}
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: '12px',
        overflowX: 'auto',
        paddingBottom: '8px',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        marginBottom: selectedTopCategory ? '24px' : '16px'
      }}>
        {isLoading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <ShimmerCard key={`top-shimmer-${index}`} />
          ))
        ) : (
          topCategories.map((topCat, index) => (
            <TopCategoryCard
              key={topCat.id}
              category={topCat}
              isSelected={selectedTopCategory === topCat.id}
              index={index}
              onClick={handleTopCategorySelect}
              count={topCat.count}
            />
          ))
        )}
      </div>

      {/* PRIMARY CATEGORIES SECTION */}
      <AnimatePresence>
        {selectedTopCategory && primaryCategories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            style={{ marginBottom: '20px' }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#111827',
                margin: 0
              }}>
                {t('category.categories')}
                {selectedPrimaryCategories.length > 0 && (
                  <span style={{
                    marginLeft: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#f59e0b',
                    backgroundColor: '#fef3c7',
                    padding: '2px 8px',
                    borderRadius: '12px'
                  }}>
                    {t('category.selected', { count: selectedPrimaryCategories.length })}
                  </span>
                )}
              </h3>
              <div style={{
                fontSize: '12px',
                color: '#6b7280',
                backgroundColor: '#f3f4f6',
                padding: '4px 8px',
                borderRadius: '12px'
              }}>
                {t('category.types', { count: primaryCategories.length })}
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              overflowX: 'auto',
              paddingBottom: '8px',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              marginBottom: selectedPrimaryCategories.length > 0 ? '24px' : '0'
            }}>
              {primaryCategories
                .map((category, index) => {
                  const isSelected = selectedPrimaryCategories.includes(category.id);
                  const isFitnessWellness = FITNESS_WELLNESS_CATEGORIES.includes(category.title);
                  const hasSelectedFitness = selectedPrimaryCategories.some(id => {
                    const cat = primaryCategories.find(c => c.id === id);
                    return cat && FITNESS_WELLNESS_CATEGORIES.includes(cat.title);
                  });
                  const hasSelectedNonFitness = selectedPrimaryCategories.some(id => {
                    const cat = primaryCategories.find(c => c.id === id);
                    return cat && !FITNESS_WELLNESS_CATEGORIES.includes(cat.title);
                  });
                  const isDisabled = (hasSelectedFitness && !isFitnessWellness && !isSelected) ||
                                     (hasSelectedNonFitness && isFitnessWellness && !isSelected);
                  
                  return {
                    ...category,
                    isSelected,
                    isDisabled,
                    originalIndex: index
                  };
                })
                .sort((a, b) => {
                  if (a.isSelected && !b.isSelected) return -1;
                  if (!a.isSelected && b.isSelected) return 1;
                  if (a.isSelected && b.isSelected) return a.originalIndex - b.originalIndex;
                  if (!a.isSelected && !b.isSelected) {
                    if (a.isDisabled && !b.isDisabled) return 1;
                    if (!a.isDisabled && b.isDisabled) return -1;
                    return a.originalIndex - b.originalIndex;
                  }
                  return 0;
                })
                .map((category, index) => (
                  <PrimaryCategoryCard
                    key={category.id}
                    category={category}
                    isSelected={category.isSelected}
                    isDisabled={category.isDisabled}
                    index={index}
                    onClick={handlePrimaryCategorySelect}
                  />
                ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SUB-CATEGORIES SECTION */}
      <AnimatePresence>
        {selectedPrimaryCategories.length > 0 && subCategories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            style={{ marginBottom: '20px' }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                margin: 0
              }}>
                🏢 {t('category.venueTypes')}
              </h4>
              {selectedSubCategories.length > 0 && (
                <span style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  backgroundColor: '#fef3c7',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontWeight: '600'
                }}>
                  {t('category.selected', { count: selectedSubCategories.length })}
                </span>
              )}
              <div style={{
                fontSize: '11px',
                color: '#6b7280',
                backgroundColor: '#f3f4f6',
                padding: '2px 6px',
                borderRadius: '8px'
              }}>
                {t('category.types', { count: subCategories.length })}
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
              paddingBottom: '8px',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}>
              {subCategories
                .map((subCategory, index) => ({
                  ...subCategory,
                  isSelected: selectedSubCategories.includes(subCategory.id),
                  originalIndex: index
                }))
                .sort((a, b) => {
                  if (a.isSelected && !b.isSelected) return -1;
                  if (!a.isSelected && b.isSelected) return 1;
                  if (a.isSelected && b.isSelected) return a.originalIndex - b.originalIndex;
                  return a.originalIndex - b.originalIndex;
                })
                .map((subCategory, index) => (
                  <SubCategoryCard
                    key={subCategory.id}
                    subCategory={subCategory}
                    isSelected={subCategory.isSelected}
                    index={index}
                    onClick={handleSubCategorySelect}
                  />
                ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* QUICK FILTERS SECTION */}
      <AnimatePresence>
        {selectedSubCategories.length > 0 && 
         selectedPrimaryCategories.length > 0 && 
         allQuickFilters.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <h4 style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#374151',
                margin: 0
              }}>
                ⚡ {t('category.quickFilters')}
                {selectedQuickFilters.length > 0 && (
                  <span style={{
                    marginLeft: '6px',
                    fontSize: '9px',
                    fontWeight: '600',
                    color: '#f59e0b',
                    backgroundColor: '#fef3c7',
                    padding: '2px 6px',
                    borderRadius: '8px'
                  }}>
                    {t('category.active', { count: selectedQuickFilters.length })}
                  </span>
                )}
              </h4>
              <div style={{
                fontSize: '10px',
                color: '#6b7280',
                backgroundColor: '#e5e7eb',
                padding: '2px 6px',
                borderRadius: '8px'
              }}>
                {t('category.filters', { count: allQuickFilters.length })}
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '6px',
              overflowX: 'auto',
              padding: '4px 0',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}>
              {allQuickFilters
                .map((filter, index) => ({
                  ...filter,
                  isSelected: selectedQuickFilters.includes(filter.id),
                  originalIndex: index
                }))
                .sort((a, b) => {
                  if (a.isSelected && !b.isSelected) return -1;
                  if (!a.isSelected && b.isSelected) return 1;
                  if (a.isSelected && b.isSelected) return a.originalIndex - b.originalIndex;
                  return a.originalIndex - b.originalIndex;
                })
                .map((filter, index) => {
                  const isExpanded = expandedParentFilters.includes(filter.id);
                  
                  return (
                    <React.Fragment key={filter.id}>
                      <QuickFilterButton
                        filter={filter}
                        isSelected={filter.isSelected}
                        index={index}
                        onClick={handleQuickFilterSelect}
                        isChild={false}
                        parentId={null}
                        expandedParentFilters={expandedParentFilters}
                      />
                      
                      {filter.isParent && filter.children && isExpanded && (
                        <AnimatePresence>
                          {filter.children.map((childFilter, childIndex) => (
                            <QuickFilterButton
                              key={childFilter.id}
                              filter={childFilter}
                              isSelected={selectedQuickFilters.includes(childFilter.id)}
                              index={childIndex}
                              onClick={handleQuickFilterSelect}
                              isChild={true}
                              parentId={filter.id}
                              expandedParentFilters={expandedParentFilters}
                            />
                          ))}
                        </AnimatePresence>
                      )}
                    </React.Fragment>
                  );
                })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{
        textAlign: 'center',
        marginTop: '8px',
        fontSize: '10px',
        color: '#9ca3af'
      }}>
        {t('category.scrollHint')}
      </div>

      <style>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default memo(CategoryCarousel);